import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";
import { Booking, CouponResponse } from "../../../../../../definitions/booking";
import { PropertyRoom } from "../../../../../../definitions/property";
import useBookingService from "../../../../../../hooks/useBookingService";
import { gtag } from "../../../../../../utils/track";
import { trackZostelEvent } from "../../../../../../utils/tracking";
import DesktopBookingSummary from "./components/DesktopBookingSummary";
import MobileBookingSummary from "./components/MobileBookingSummary";

interface BookingSummaryProps {
  bookingDetails: Booking;
  rooms: Array<PropertyRoom>;
  getRoomCountSetter: (roomRef: string) => (count: number) => void;
  scrollToBooking?: () => void;
  scrollToOtherStays?: () => void;
  propertyName?: any;
  propertyInfo?: any;
  showSimilarProperty?: boolean;
  guestCount?: number;
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  bookingDetails,
  rooms,
  scrollToBooking,
  scrollToOtherStays,
  getRoomCountSetter,
  propertyName,
  showSimilarProperty,
  propertyInfo,
  guestCount,
}) => {
  const history = useHistory();
  const location = useLocation();
  const bookingService = useBookingService();

  const [couponResponse, setCouponResponse] = useState<
    CouponResponse | undefined
  >();
  const [fetching, setFetching] = useState<boolean>(false);
  const [overTwoMonths, setOverTwoMonths] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState<string>("");

  const totalNights: number = useMemo(
    () =>
      moment(bookingDetails.checkout).diff(
        moment(bookingDetails.checkin),
        "days"
      ),
    [bookingDetails]
  );

  const handleApplyCoupon = (code: string) => {
    setCouponCode(code);
  };

  const handleRemoveCoupon = (code: string) => {
    setCouponCode(code);
  };

  useEffect(() => {
    if (bookingDetails.rooms.length) {
      const updatedBookingDetails = {
        ...bookingDetails,
        coupon_code: couponCode,
      };
      setFetching(true);
      bookingService
        .applyCoupon(updatedBookingDetails)
        .then((res) => {
          if (res) {
            // console.log("Coupon:", res.data);
            const rooms = bookingService.getRoomDataFromBooking(
              res.data.booking.rooms
            );
            setCouponResponse({ ...res.data.booking, rooms });
            if (
              couponCode &&
              res.data.booking &&
              res.data.booking.discount === 0
            ) {
              setCouponMessage("invalid");
            } else if (couponCode && res.data.booking) {
              setCouponMessage("valid");
            }
            // console.log(rooms);
          }
          setFetching(false);
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      setCouponResponse(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDetails, couponCode]);

  const getRemoveRoomHandler = (roomRef: string) => {
    return () => {
      getRoomCountSetter(roomRef)(0);
    };
  };

  useEffect(() => {
    // console.log("Booking Details", bookingDetails);
    const dateDiff = moment(bookingDetails.checkout, "YYYY-MM-DD").diff(
      moment(),
      "days"
    );
    setOverTwoMonths(dateDiff > 60 ? true : false);
  }, [bookingDetails]);

  const isMoveIn = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("movein") && queryParams.get("movein") === "true";
  }, [location.search]);

  const handleBook = () => {
    const updatedBookingDetails = {
      ...bookingDetails,
      coupon_code: couponCode,
    };
    const encodedJSON = bookingService.getURIFromBookingDetails(
      updatedBookingDetails
    );

    let roomValues = "";
    bookingDetails.rooms.forEach((room, index) => {
      const roomData = rooms.find((_room) => _room._ref === room._ref);
      roomValues += `${roomData?.name} * ${room.count} * ${roomData?.nights}`;
    });
    const eventData = {
      checkin: bookingDetails.checkin,
      checkout: bookingDetails.checkout,
      property_code: bookingDetails.property_code,
      payableNow: couponResponse && couponResponse.advance_amount,
      value: roomValues,
    };
    gtag("event", "Property_BookNow_Click", { eventData });
    trackZostelEvent("Property_BookNow_Click", { eventData });
    history.push(
      "/booking?" +
        (isMoveIn ? "movein=true&" : "") +
        encodedJSON +
        "&guests=" +
        guestCount
    );
  };

  return window.innerWidth < 640 ? (
    <MobileBookingSummary
      bookingDetails={bookingDetails}
      couponResponse={couponResponse}
      fetching={fetching}
      getRemoveRoomHandler={getRemoveRoomHandler}
      handleBook={handleBook}
      rooms={rooms}
      totalNights={totalNights}
      scrollToBooking={scrollToBooking}
      scrollToOtherStays={scrollToOtherStays}
      overTwoMonths={overTwoMonths}
      propertyName={propertyName}
      showSimilarProperty={showSimilarProperty}
      onApplyCoupon={handleApplyCoupon}
      onRemoveCoupon={handleRemoveCoupon}
      couponMessage={couponMessage}
      setCouponMessage={setCouponMessage}
      guestCount={guestCount}
    />
  ) : (
    <DesktopBookingSummary
      bookingDetails={bookingDetails}
      propertyInfo={propertyInfo}
      couponResponse={couponResponse}
      fetching={fetching}
      getRemoveRoomHandler={getRemoveRoomHandler}
      handleBook={handleBook}
      rooms={rooms}
      totalNights={totalNights}
      overTwoMonths={overTwoMonths}
      onApplyCoupon={handleApplyCoupon}
      onRemoveCoupon={handleRemoveCoupon}
      couponMessage={couponMessage}
      setCouponMessage={setCouponMessage}
      guestCount={guestCount}
    />
  );
};

export default BookingSummary;
